import * as React from 'react';
import { showFavoritesFoldersList, showModalCreateFolder } from '@cian/favorites-assign-widget';
import { hideNotification } from '@cian/favorites-assign-widget/es/shared/actions';
import { useDeviceType } from '@cian/ui-kit';

import { FavoriteButton, FavoriteTooltip } from '../../components/FavoriteButtonDesktop';
import { selectIsAuthenticated } from '../../selectors/isAuthenticated';
import { selectRecommendationById } from '../../selectors/recommendations';
import { addToFavorite, deleteFromFavorite, isAddToFavoriteRequestError, requestAuthentication } from '../../thunks';
import { useAppDispatch, useAppSelector as useSelector } from '../../types';
import { toUnknownAction } from '../../utils/redux/toUnknownAction';
import { trackFavoritesLinkClick } from './tracking';

const TOOLTIP_CLOSE_TIMEOUT = 6000;

type TFavoriteButtonDesktopContainerProps = {
  id: number | null;
};

export const FavoriteButtonDesktopContainer: React.FC<TFavoriteButtonDesktopContainerProps> = ({ id }) => {
  const dispatch = useAppDispatch();
  const deviceType = useDeviceType();
  const [isErrorTooltipOpen, setErrorTooltipOpen] = React.useState(false);
  const favoriteActionVersionRef = React.useRef(0);
  const recommendation = useSelector(selectRecommendationById(id));
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { savedOfferId, showNotification, userHasFolders } = useSelector(state => state.favoritesAssignWidget);

  const isSuccessTooltipOpen = Boolean(id && showNotification && savedOfferId.length === 1 && savedOfferId[0] === id);
  const isTooltipOpen = isSuccessTooltipOpen || isErrorTooltipOpen;

  const handleCloseTooltip = React.useCallback(() => {
    if (isErrorTooltipOpen) {
      setErrorTooltipOpen(false);

      return;
    }

    if (isSuccessTooltipOpen) {
      dispatch(toUnknownAction(hideNotification()));
    }
  }, [dispatch, isErrorTooltipOpen, isSuccessTooltipOpen]);

  React.useEffect(() => {
    if (!isTooltipOpen) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      handleCloseTooltip();
    }, TOOLTIP_CLOSE_TIMEOUT);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [handleCloseTooltip, isTooltipOpen]);

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      if (!recommendation) {
        return;
      }

      const actionVersion = favoriteActionVersionRef.current + 1;
      favoriteActionVersionRef.current = actionVersion;

      if (recommendation.isFavorite) {
        setErrorTooltipOpen(false);
        dispatch(toUnknownAction(hideNotification()));
        dispatch(
          deleteFromFavorite({
            recommendation,
            deviceType,
            rollbackOnError: true,
            showDeleteOnboarding: false,
          }),
        );

        return;
      }

      setErrorTooltipOpen(false);

      void dispatch(addToFavorite({ recommendation, deviceType, rollbackOnError: true }))
        .unwrap()
        .catch((error: unknown) => {
          if (favoriteActionVersionRef.current === actionVersion && isAddToFavoriteRequestError(error)) {
            setErrorTooltipOpen(true);
          }
        });
    },
    [deviceType, dispatch, recommendation],
  );

  const handleWrapperClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleLogin = React.useCallback(() => {
    dispatch(requestAuthentication({}));
  }, [dispatch]);

  const handleAddToFolder = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      dispatch(toUnknownAction(hideNotification()));

      if (!id) {
        return;
      }

      if (!isAuthenticated) {
        handleLogin();

        return;
      }

      if (userHasFolders) {
        dispatch(toUnknownAction(showFavoritesFoldersList({ offerIds: [id] })));

        return;
      }

      dispatch(toUnknownAction(showModalCreateFolder({ offerIds: [id] })));
    },
    [dispatch, handleLogin, id, isAuthenticated, userHasFolders],
  );

  const handleFavoritesLinkClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();

      if (id) {
        trackFavoritesLinkClick(id);
      }
    },
    [id],
  );

  if (!id || !recommendation) {
    return null;
  }

  return (
    <div onClick={handleWrapperClick}>
      <FavoriteTooltip
        isOpen={isTooltipOpen}
        variant={isErrorTooltipOpen ? 'error' : 'success'}
        onAddToFolder={handleAddToFolder}
        onClose={handleCloseTooltip}
        onFavoritesLinkClick={handleFavoritesLinkClick}
      >
        <FavoriteButton isActive={recommendation.isFavorite} onClick={handleClick} />
      </FavoriteTooltip>
    </div>
  );
};
